import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const STATUS_OPTIONS = ['draft', 'submitted', 'under_review', 'approved', 'accepted', 'rejected'];
const STATUS_BADGES = {
  draft: 'badge-gray', submitted: 'badge-blue', under_review: 'badge-yellow',
  approved: 'badge-green', accepted: 'badge-green', rejected: 'badge-red', pending: 'badge-yellow'
};

const EMPTY_PROPOSAL = {
  title: '', opportunity_id: '', estimate_id: '', valid_until: '',
  document_url: '', notes: ''
};

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PROPOSAL });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { dirty, markDirty, resetDirty } = useFormDirty();

  // Approval view
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [approvalComment, setApprovalComment] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [propRes, oppRes, estRes] = await Promise.all([
        apiFetch('/api/proposals'),
        apiFetch('/api/crm/opportunities?limit=200'),
        apiFetch('/api/estimates/effort-estimates')
      ]);
      if (propRes.ok) { const d = await propRes.json(); setProposals(d.proposals || []); }
      if (oppRes.ok) { const d = await oppRes.json(); setOpportunities(d.opportunities || []); }
      if (estRes.ok) { const d = await estRes.json(); setEstimates(d.estimates || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openCreate() {
    setEditing(null); setForm({ ...EMPTY_PROPOSAL }); setErrors({}); resetDirty(); setModalOpen(true);
  }

  function openEdit(prop) {
    setEditing(prop);
    setForm({
      title: prop.title || '', opportunity_id: prop.opportunity_id || '',
      estimate_id: prop.estimate_id || '',
      valid_until: prop.valid_until ? prop.valid_until.substring(0, 10) : '',
      document_url: prop.document_url || '', notes: prop.notes || ''
    });
    setErrors({}); resetDirty(); setModalOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { setErrors({ title: 'Title is required' }); return; }
    setSaving(true);
    const body = {
      title: form.title, opportunityId: form.opportunity_id || null,
      estimateId: form.estimate_id || null, validUntil: form.valid_until || null,
      documentUrl: form.document_url || null, notes: form.notes || null
    };
    const url = editing ? `/api/proposals/${editing.id}` : '/api/proposals';
    const method = editing ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { resetDirty(); setModalOpen(false); fetchData(); }
      else { const d = await res.json(); setErrors({ _general: d.error || 'Failed to save' }); }
    } catch (e) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleDelete(prop) {
    if (!window.confirm(`Delete proposal "${prop.title}"?`)) return;
    try { await apiFetch(`/api/proposals/${prop.id}`, { method: 'DELETE' }); fetchData(); } catch (e) { /* ignore */ }
  }

  async function handleSubmit(prop) {
    if (!window.confirm(`Submit proposal "${prop.title}" for review?`)) return;
    try {
      const res = await apiFetch(`/api/proposals/${prop.id}/submit`, { method: 'POST' });
      if (res.ok) fetchData();
    } catch (e) { /* ignore */ }
  }

  async function handleDuplicate(prop) {
    try {
      const res = await apiFetch(`/api/proposals/${prop.id}/duplicate`, { method: 'POST' });
      if (res.ok) fetchData();
    } catch (e) { /* ignore */ }
  }

  // ---- Approval workflow ----
  async function openApprovals(prop) {
    setSelectedProposal(prop);
    setApprovalComment('');
    try {
      const res = await apiFetch(`/api/proposals/${prop.id}/approvals`);
      if (res.ok) { const d = await res.json(); setApprovals(d.approvals || []); }
    } catch (e) { /* ignore */ }
  }

  function closeApprovals() { setSelectedProposal(null); setApprovals([]); }

  async function handleApprove() {
    try {
      await apiFetch(`/api/proposals/${selectedProposal.id}/approve`, {
        method: 'POST', body: JSON.stringify({ comments: approvalComment })
      });
      openApprovals(selectedProposal);
      fetchData();
    } catch (e) { /* ignore */ }
  }

  async function handleReject() {
    if (!approvalComment.trim()) { alert('Please provide a reason for rejection.'); return; }
    try {
      await apiFetch(`/api/proposals/${selectedProposal.id}/reject`, {
        method: 'POST', body: JSON.stringify({ comments: approvalComment })
      });
      openApprovals(selectedProposal);
      fetchData();
    } catch (e) { /* ignore */ }
  }

  const oppOptions = opportunities.map(o => ({ value: o.id, label: o.name }));
  const estOptions = estimates.map(e => ({ value: e.id, label: `${e.name} (v${e.version || 1})` }));

  // ---- Approval detail view ----
  if (selectedProposal) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: -40 }}>
          <button className="btn btn-secondary" onClick={closeApprovals}>Back to Proposals</button>
          <h3 style={{ margin: 0 }}>{selectedProposal.title} (v{selectedProposal.version || 1})</h3>
          <span className={`badge ${STATUS_BADGES[selectedProposal.status] || 'badge-gray'}`}>{selectedProposal.status}</span>
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Proposal Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            <div><strong>Opportunity:</strong> {selectedProposal.opportunity_name || '--'}</div>
            <div><strong>Estimate:</strong> {selectedProposal.estimate_name || '--'}</div>
            <div><strong>Submitted:</strong> {selectedProposal.submitted_date ? formatDate(selectedProposal.submitted_date) : '--'}</div>
            <div><strong>Valid Until:</strong> {selectedProposal.valid_until ? formatDate(selectedProposal.valid_until) : '--'}</div>
            {selectedProposal.notes && <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {selectedProposal.notes}</div>}
          </div>
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Approval Chain</h4>
          {approvals.length === 0 ? (
            <p style={{ color: '#64748B' }}>No approvals recorded yet.</p>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Approver</th><th>Role</th><th>Status</th><th>Comments</th><th>Date</th></tr>
              </thead>
              <tbody>
                {approvals.map(a => (
                  <tr key={a.id}>
                    <td>{a.approver_name || a.approver_email || '--'}</td>
                    <td>{a.role || '--'}</td>
                    <td><span className={`badge ${STATUS_BADGES[a.status] || 'badge-gray'}`}>{a.status}</span></td>
                    <td>{a.comments || '--'}</td>
                    <td>{a.acted_at ? formatDate(a.acted_at) : '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {(selectedProposal.status === 'submitted' || selectedProposal.status === 'under_review') && (
          <div className="card" style={{ padding: 20 }}>
            <h4 style={{ marginTop: 0 }}>Your Decision</h4>
            <FormField label="Comments" name="approvalComment" type="textarea" value={approvalComment} onChange={(n, v) => setApprovalComment(v)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleApprove}>Approve</button>
              <button className="btn" style={{ background: '#EF4444', color: '#fff' }} onClick={handleReject}>Reject</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const columns = [
    { key: 'title', label: 'Title', sortable: true },
    { key: 'opportunity_name', label: 'Opportunity', sortable: true, render: (v) => v || '--' },
    { key: 'estimate_name', label: 'Estimate', sortable: true, render: (v) => v || '--' },
    { key: 'version', label: 'Ver', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> },
    { key: 'submitted_date', label: 'Submitted', sortable: true, render: (v) => v ? formatDate(v) : '--' },
    { key: 'valid_until', label: 'Valid Until', sortable: true, render: (v) => v ? formatDate(v) : '--' }
  ];

  const actions = [
    { label: 'View', onClick: openApprovals },
    { label: 'Edit', onClick: openEdit },
    { label: 'Document', icon: 'document', href: 'document_url', className: 'action-btn--link' },
    { label: 'Submit', icon: 'submit', onClick: handleSubmit, className: 'action-btn--success' },
    { label: 'Duplicate', onClick: handleDuplicate },
    { label: 'Delete', onClick: handleDelete }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, marginTop: -40 }}>
        <button className="btn btn-primary" onClick={openCreate}>+ New Proposal</button>
      </div>

      <DataTable
        columns={columns}
        data={proposals}
        loading={loading}
        actions={actions}
        emptyMessage="No proposals found. Create your first proposal."
      />

      <Modal title={editing ? 'Edit Proposal' : 'New Proposal'} isOpen={modalOpen} onClose={() => setModalOpen(false)} size="lg" dirty={dirty}>
        {errors._general && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>}
        <FormField label="Title" name="title" value={form.title} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.title} required />
        <div className="form-row">
          <FormField label="Opportunity" name="opportunity_id" type="select" value={form.opportunity_id} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={oppOptions} placeholder="Select opportunity..." />
          <FormField label="Estimate" name="estimate_id" type="select" value={form.estimate_id} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={estOptions} placeholder="Select estimate..." />
        </div>
        <div className="form-row">
          <FormField label="Valid Until" name="valid_until" type="date" value={form.valid_until} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Document URL" name="document_url" value={form.document_url} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <FormField label="Notes" name="notes" type="textarea" value={form.notes} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Create')}</button>
        </div>
      </Modal>
    </div>
  );
}
